/**
 * Editor covers (#929 / #930) — ONE mechanism for "something covers this editor without unmounting it".
 *
 * Two shapes exist today:
 *   - a VERSION PREVIEW: `absolute inset-0 z-10` over the still-mounted, still-editable live editor (the workspace
 *     tab and the single-file view own those overlays);
 *   - the conflict view's "SAVED VERSION" panel: the live editor `display:none` behind a read-only copy (the collab
 *     office editors own that panel).
 *
 * Whoever covers REPORTS it — the overlay's owner, or the editor that hides itself — because they are the ones that
 * know the cover is up (the lesson of `useUndoScopeGuard`). The editor host (`TabContent`, `SingleFileView`) collects
 * the reports and ENFORCES them in the two shared places a covered editor was still reached through:
 *   1. the command registry — it marks its editor scope covered (`coverScope`), so every editor's OWN commands
 *      (Delete Rows, Copy cells…) resolve disabled, in the menus and for shortcuts, whatever the editor registered;
 *   2. the shell's Save gates (Cmd+S, the File menu, `file.save`) — the host passes the cover up, and Save is
 *      refused with the cover's own notice instead of writing the hidden document.
 *
 * The version preview is the HOST's own cover (it owns the overlay), so it is folded in by the host rather than
 * reported; every other cover reports through {@link useCoverEditor}. A new editor needs nothing to be covered
 * correctly.
 * Undo stays where it is (`useUndoScopeGuard` / the host's decliner): it is routed by region, not by command scope.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'

/** What a cover tells the shell — enough to refuse a save honestly and offer the way out. */
export interface EditorCover {
  /** The notice shown when a save is refused under this cover (already in the user's language). */
  message: string
  /** The notice's action label (already in the user's language). */
  actionLabel: string
  /** Takes the cover away, back to the live editor. */
  uncover: () => void
}

interface EditorCoverReporter {
  report: (key: object, cover: EditorCover | null) => void
  /** Whether anything covers this host's editor right now (the host's own answer — a preview included). */
  covered: boolean
}

const EditorCoverContext = createContext<EditorCoverReporter | null>(null)

/**
 * Collects the covers reported below it and hands the current one (the most recent still up, or null) to `onChange`.
 * Rendered by an editor host around its editor.
 */
export function EditorCoverProvider({ onChange, covered = false, children }: {
  onChange: (cover: EditorCover | null) => void
  /** The host's answer to "is anything covering the editor" — read by {@link useEditorCovered}. */
  covered?: boolean
  children: ReactNode
}) {
  const coversRef = useRef(new Map<object, EditorCover>())
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const report = useCallback((key: object, cover: EditorCover | null) => {
    const covers = coversRef.current
    if (cover) {
      covers.delete(key)   // re-inserted last: the most recent report wins
      covers.set(key, cover)
    } else if (!covers.delete(key)) {
      return
    }
    const all = [...covers.values()]
    onChangeRef.current(all.length > 0 ? all[all.length - 1]! : null)
  }, [])
  const value = useMemo<EditorCoverReporter>(() => ({ report, covered }), [report, covered])
  return <EditorCoverContext.Provider value={value}>{children}</EditorCoverContext.Provider>
}

/**
 * Report that this component covers the editor while `cover` is non-null. Outside an editor host it does nothing
 * (a share view, a preview rendered on its own). Re-reports when the cover's content changes; withdraws on unmount.
 */
export function useCoverEditor(cover: EditorCover | null): void {
  const reporter = useContext(EditorCoverContext)
  const key = useRef({}).current
  const on = cover !== null
  // The callbacks a cover carries are usually fresh closures; read through a ref so a re-render doesn't re-report.
  const coverRef = useRef(cover)
  coverRef.current = cover
  const message = cover?.message
  const actionLabel = cover?.actionLabel
  const report = reporter?.report
  useEffect(() => {
    if (!report || !on) return
    report(key, {
      message: message!,
      actionLabel: actionLabel!,
      uncover: () => coverRef.current?.uncover(),
    })
    return () => report(key, null)
  }, [report, key, on, message, actionLabel])
}

/**
 * Whether something covers the editor this component is inside (false outside an editor host). For an editor's OWN
 * listener that acts on the live document outside the command registry — a `window` Cmd+S, say — which must stand
 * down while it is covered, as the registry's commands do.
 */
export function useEditorCovered(): boolean {
  return useContext(EditorCoverContext)?.covered ?? false
}
